/* =============================================================================
   support-hub
   FAQ content comes from the category rich text (bold paragraph = question,
   following paragraphs = answer, a link-only paragraph = the answer's button).
   Search, FAQ index, order status tabs, topic-driven contact form and deep links
   (#faq/<cat>[/<q>], #contact/<topic>). The form is Shopify's contact form.
   ============================================================================= */
if (!customElements.get('support-hub')) {
  const STOPWORDS = new Set(
    'is mijn de het een ik je jullie wat hoe kan er van op te en of voor met in bij aan om dat die nog wel niet ook dan als zijn wordt worden moet mag'.split(' ')
  );
  const SYNONYMS = {
    pakket: ['bestelling', 'zending'], pakketje: ['pakket', 'bestelling'], order: ['bestelling'], bestelling: ['order'],
    terugsturen: ['retour', 'terug'], ruilen: ['ruil', 'retour'], kapot: ['beschadigd', 'lekt'], stuk: ['kapot', 'beschadigd'], beschadigd: ['kapot', 'schade'],
    allergie: ['allerg'], allergenen: ['allerg'], allergeen: ['allerg'], korting: ['kortingscode'], code: ['kortingscode'],
    verzendkosten: ['verzending'], levertijd: ['verzonden', 'verzend'], bezorgen: ['bezorg', 'verzend'], bezorging: ['bezorg', 'verzend'],
    betalen: ['betaal', 'betaling'], winkel: ['foodstore'], openingstijden: ['open'], annuleren: ['annuleer'], wijzigen: ['wijzig', 'aanpassen'], inloggen: ['inlog'],
  };

  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const norm = (s) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[‘’]/g, "'");
  const slug = (s) => norm(s).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const decode = (s) => {
    try {
      return decodeURIComponent(s);
    } catch (e) {
      return s;
    }
  };

  class SupportHub extends HTMLElement {
    connectedCallback() {
      this.$ = (s, el = this) => el.querySelector(s);
      this.$$ = (s, el = this) => [...el.querySelectorAll(s)];
      this.config = JSON.parse(this.$('[data-config]').textContent);
      this.topics = this.config.topics;
      this.topic = null;
      this.state = { group: 'webshop', cat: null };

      this.parseFaq();
      this.initStatus();
      this.initFaq();
      this.initSearch();
      this.initForm();

      this.addEventListener('click', (e) => this.onClick(e));
      // Shopify shows an "Are you sure you want to leave Upfront?" page for links to other sites,
      // unless the page handles the click itself. Links in this section are set in the editor
      // (return portal, PostNL app…), so open those ourselves, in a new tab.
      ['click', 'auxclick'].forEach((type) => this.addEventListener(type, (e) => this.openExternal(e)));
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.sh-select')) this.openList(false);
      });
      window.addEventListener('hashchange', () => this.route());
      this.route();
    }

    icon(name) {
      const tpl = this.$(`template[data-icon="${name}"]`);
      return tpl ? tpl.innerHTML : '';
    }

    /* ---------- content ---------- */
    parseAction(a) {
      const href = a.getAttribute('href') || '';
      const label = a.textContent.trim();
      const hash = href.slice(href.indexOf('#'));
      if (hash.startsWith('#contact/')) {
        const [topic, query] = hash.slice(9).split('?');
        const msg = query && new URLSearchParams(query).get('msg');
        return { contact: decode(topic), label, msg: msg || '' };
      }
      if (hash.startsWith('#faq/')) return { faq: decode(hash.slice(5)), label };
      let external = false;
      try {
        const url = new URL(href, location.href);
        external = url.protocol.startsWith('http') && !url.hostname.endsWith(location.hostname.replace(/^www\./, ''));
      } catch (e) {}
      return { link: label, href, ext: external };
    }

    parseFaq() {
      this.cats = [];
      this.all = [];
      this.$$('[data-source] [data-category]').forEach((el) => {
        const cat = { handle: el.dataset.category, group: el.dataset.group, title: el.dataset.title, qs: [] };
        let current = null;
        [...el.children].forEach((node) => {
          const text = node.textContent.trim();
          if (!text) return;
          const strong = node.tagName === 'P' && node.querySelector('strong, b');
          const links = node.querySelectorAll('a');
          if (strong && strong.textContent.trim() === text) {
            current = { q: text, html: '', acts: [] };
            current.id = slug(text);
            cat.qs.push(current);
          } else if (current && links.length === 1 && links[0].textContent.trim() === text) {
            current.acts.push(this.parseAction(links[0]));
          } else if (current) {
            current.html += node.outerHTML;
          }
        });
        cat.qs.forEach((q) => {
          q.cat = cat;
          q.group = cat.group;
          q.uid = `${cat.handle}--${q.id}`;
          q.text = new DOMParser().parseFromString(q.html, 'text/html').body.textContent;
          q.nq = norm(q.q);
          q.na = norm(q.text);
          this.all.push(q);
        });
        if (cat.qs.length) this.cats.push(cat);
      });
    }

    actionHTML(act, cls = 'btn-o') {
      if (!act) return '';
      const arrow = this.icon('arrow');
      if (act.link) {
        return `<a class="${cls}${act.mobileOnly ? ' m-only' : ''}" href="${esc(act.href)}"${act.ext ? ' target="_blank" rel="noopener"' : ''}><span>${esc(act.link)}</span>${act.ext ? this.icon('ext') : arrow}</a>`;
      }
      if (act.contact) {
        return `<button type="button" class="${cls}" data-contact="${esc(act.contact)}"${act.msg ? ` data-msg="${esc(act.msg)}"` : ''}><span>${esc(act.label)}</span>${arrow}</button>`;
      }
      if (act.faq) return `<button type="button" class="${cls}" data-faq="${esc(act.faq)}"><span>${esc(act.label)}</span>${arrow}</button>`;
      return '';
    }

    highlight(html, vars, isHTML) {
      const text = isHTML ? html : esc(html);
      if (!vars || !vars.length) return text;
      const re = new RegExp(
        '(' + [...new Set(vars)].sort((a, b) => b.length - a.length).map((v) => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|') + ')',
        'gi'
      );
      // only touch text between tags and entities
      return text
        .split(/(<[^>]+>|&[#a-z0-9]+;)/i)
        .map((part, i) => (i % 2 ? part : part.replace(re, '<mark>$1</mark>')))
        .join('');
    }

    accHTML(item, { label = '', open = false, hl = [] } = {}) {
      const pid = `${this.id || 'sh'}-p-${item.uid}`;
      return `<div class="acc${open ? ' is-open' : ''}" id="q-${esc(item.uid)}" data-uid="${esc(item.uid)}">
        <button class="acc-trigger" type="button" aria-expanded="${open}" aria-controls="${esc(pid)}">
          <span class="acc-q"><span class="acc-text t-b3m">${this.highlight(item.q, hl)}</span>${label ? `<span class="t-b6 c4 caps">${esc(label)}</span>` : ''}</span>
          ${this.icon('add').replace('class="ic ', 'class="ic i-add ')}${this.icon('sub').replace('class="ic ', 'class="ic i-sub ')}
        </button>
        <div class="acc-panel" id="${esc(pid)}" role="region"><div><div class="acc-inner"><div class="acc-answer t-b4 c2">${this.highlight(item.html, hl, true)}</div>${item.acts.length ? `<div class="acc-actions">${item.acts.map((a) => this.actionHTML(a)).join('')}</div>` : ''}</div></div></div>
      </div>`;
    }

    toggleAcc(acc, open = !acc.classList.contains('is-open')) {
      acc.classList.toggle('is-open', open);
      this.$('.acc-trigger', acc).setAttribute('aria-expanded', open);
    }

    /* ---------- order status ---------- */
    initStatus() {
      this.$$('[data-actions]').forEach((box) => {
        const html = this.$$('a', box)
          .map((a) => {
            const act = this.parseAction(a);
            if (a.closest('[data-mobile-only]')) act.mobileOnly = true;
            return this.actionHTML(act, 'link');
          })
          .join('');
        box.innerHTML = html;
      });
      this.$$('.sh-seg .chip').forEach((btn) =>
        btn.addEventListener('click', () => {
          this.$$('.sh-seg .chip').forEach((b) => {
            b.classList.toggle('is-sel', b === btn);
            b.setAttribute('aria-selected', b === btn);
          });
          this.$$('.sh-card').forEach((c) => c.classList.toggle('is-active', c.dataset.card === btn.dataset.status));
        })
      );
    }

    /* ---------- FAQ ---------- */
    initFaq() {
      const groups = new Set(this.cats.map((c) => c.group));
      this.$$('.sh-toggle [data-group]').forEach((b) => (b.hidden = !groups.has(b.dataset.group)));
      if (groups.size < 2) this.$('.sh-toggle').hidden = true;
      if (!groups.has('webshop') && groups.size) this.state.group = [...groups][0];
      this.$('.sh-toggle').addEventListener('click', (e) => {
        const b = e.target.closest('[data-group]');
        if (!b) return;
        const deep = this.scrolledIntoFaq();
        this.state.group = b.dataset.group;
        this.state.cat = null;
        this.renderFaq();
        this.setHash(`#faq/${this.state.cat}`);
        if (deep) this.scrollToSection(this.$('#faq h2'));
      });
      const onCat = (e) => {
        const b = e.target.closest('[data-cat]');
        if (!b) return;
        const deep = this.scrolledIntoFaq();
        this.state.cat = b.dataset.cat;
        this.renderFaq();
        this.setHash(`#faq/${this.state.cat}`);
        // switching from a long list you scrolled down in: a short one would leave you
        // looking at the section below, so go back to the top of the FAQ
        if (deep) this.scrollToSection(this.$('#faq h2'));
      };
      this.$('[data-index]').addEventListener('click', onCat);
      this.$('[data-chips]').addEventListener('click', onCat);
      this.renderFaq();
    }

    scrolledIntoFaq() {
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      return this.$('[data-faq-list]').getBoundingClientRect().top < header;
    }

    renderFaq() {
      const cats = this.cats.filter((c) => c.group === this.state.group);
      const cat = cats.find((c) => c.handle === this.state.cat) || cats[0];
      if (!cat) return;
      this.state.cat = cat.handle;
      this.$$('.sh-toggle .chip').forEach((b) => {
        const on = b.dataset.group === this.state.group;
        b.classList.toggle('is-sel', on);
        b.setAttribute('aria-selected', on);
      });
      this.$('[data-index]').innerHTML = cats
        .map(
          (c) =>
            `<button type="button" class="idx t-b4${c === cat ? ' is-active' : ''}" data-cat="${esc(c.handle)}" aria-current="${c === cat}"><span>${esc(c.title)}</span></button>`
        )
        .join('');
      this.$('[data-chips]').innerHTML = cats
        .map(
          (c) =>
            `<button type="button" class="chip${c === cat ? ' is-sel' : ''}" role="tab" aria-selected="${c === cat}" data-cat="${esc(c.handle)}">${esc(c.title)}</button>`
        )
        .join('');
      const banner = this.$('[data-banner]');
      if (banner) banner.classList.toggle('is-hidden', this.state.group !== 'webshop');
      this.$('[data-cat-title]').textContent = (cat.group === 'foodstore' ? this.config.foodstorePrefix : '') + cat.title;
      this.$('[data-faq-list]').innerHTML = cat.qs.map((q) => this.accHTML(q)).join('');
      const chip = this.$('[data-chips] .is-sel');
      const strip = this.$('[data-chips]');
      if (chip && strip.scrollWidth > strip.clientWidth) strip.scrollLeft = chip.offsetLeft - 12;
    }

    openFaq(path) {
      const [catId, qPart] = path.split('/');
      const inCat = this.all.filter((x) => x.cat.handle === catId);
      const q = qPart ? inCat.find((x) => x.id === qPart) || inCat.find((x) => x.id.includes(qPart)) : inCat[0];
      if (!q) return;
      this.state.group = q.group;
      this.state.cat = catId;
      this.renderFaq();
      const target = qPart ? document.getElementById(`q-${q.uid}`) : this.$('#faq');
      if (qPart) this.toggleAcc(target, true);
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    setHash(h) {
      history.replaceState(null, '', h);
    }

    /* ---------- search ---------- */
    initSearch() {
      this.q = this.$('[data-q]');
      this.results = this.$('[data-results]');
      this.qClear = this.$('[data-q-clear]');
      let t;
      this.q.addEventListener('input', () => {
        clearTimeout(t);
        t = setTimeout(() => this.renderResults(), 120);
      });
      this.q.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.q.value = '';
          this.renderResults();
        }
      });
      this.qClear.addEventListener('click', () => {
        this.q.value = '';
        this.renderResults();
        this.q.focus();
      });
      this.$$('[data-popular]').forEach((b) =>
        b.addEventListener('click', () => {
          this.q.value = b.dataset.popular;
          this.renderResults();
        })
      );
      const mq = matchMedia('(max-width: 767px)');
      const desktop = this.q.placeholder;
      const setPh = () => (this.q.placeholder = mq.matches && this.q.dataset.placeholderMobile ? this.q.dataset.placeholderMobile : desktop);
      mq.addEventListener('change', setPh);
      setPh();
    }

    search(raw) {
      const tokens = norm(raw).split(/[^a-z0-9€]+/).filter((t) => t.length > 1 && !STOPWORDS.has(t));
      if (!tokens.length) return { vars: [], hits: [] };
      const vs = tokens.map((t) => {
        const v = new Set([t]);
        if (t.length >= 7) v.add(t.slice(0, 6));
        (SYNONYMS[t] || []).forEach((s) => v.add(s));
        return [...v];
      });
      const hits = [];
      this.all.forEach((item, idx) => {
        let score = 0;
        let inQ = false;
        for (const [i, vars] of vs.entries()) {
          const q = vars.some((v) => item.nq.includes(v));
          const a = vars.some((v) => item.na.includes(v));
          if (!q && !a) return;
          // the word as typed beats a synonym or stem match
          score += q ? (item.nq.includes(tokens[i]) ? 4 : 3) : 1;
          inQ = inQ || q;
        }
        hits.push({ item, score, inQ, idx });
      });
      hits.sort((x, y) => y.score - x.score || x.idx - y.idx);
      return { vars: vs.flat(), hits };
    }

    renderResults() {
      const raw = this.q.value.trim();
      this.qClear.classList.toggle('is-hidden', !raw);
      if (raw.length < 2) {
        this.results.classList.add('is-hidden');
        this.results.innerHTML = '';
        return;
      }
      const { vars, hits } = this.search(raw);
      const msg = `Ik zocht naar: “${raw}”\n\n`;
      this.results.classList.remove('is-hidden');
      if (!hits.length) {
        this.results.innerHTML = `<div class="sh-results-head"><p class="t-b5 c3">Geen resultaten voor ‘${esc(raw)}’</p></div>
          <div class="sh-results-foot" style="padding-top:16px"><span class="t-b5 c3">${this.form ? 'We zetten je zoekwoord alvast in je bericht.' : 'Neem contact met ons op.'}</span>${this.actionHTML({ contact: 'Anders', label: 'Stuur een bericht', msg })}</div>`;
        return;
      }
      this.results.innerHTML = `<div class="sh-results-head"><p class="t-b5 c3">${hits.length} ${hits.length === 1 ? 'resultaat' : 'resultaten'} voor ‘${esc(raw)}’</p></div>
        <div>${hits
          .map((h, i) =>
            this.accHTML(h.item, {
              open: i === 0,
              hl: vars,
              label: `${h.item.group === 'foodstore' ? this.config.foodstorePrefix : ''}${h.item.cat.title}${h.inQ ? '' : ' · gevonden in antwoord'}`,
            })
          )
          .join('')}</div>
        <div class="sh-results-foot"><span class="t-b5 c3">Niet gevonden wat je zocht?${this.form ? ' Je zoekwoord staat al in je bericht.' : ''}</span>${this.actionHTML({ contact: 'Anders', label: 'Stuur een bericht', msg }, 'link')}</div>`;
    }

    openExternal(e) {
      if (e.defaultPrevented || (e.type === 'auxclick' && e.button !== 1)) return;
      const a = e.target.closest('a[href]');
      if (!a || !this.contains(a)) return;
      let url;
      try {
        url = new URL(a.getAttribute('href'), location.href);
      } catch (err) {
        return;
      }
      if (!/^https?:$/.test(url.protocol) || url.hostname.replace(/^www\./, '') === location.hostname.replace(/^www\./, '')) return;
      e.preventDefault();
      window.open(url.href, '_blank', 'noopener');
    }

    /* ---------- clicks + routes ---------- */
    onClick(e) {
      const trigger = e.target.closest('.acc-trigger');
      if (trigger) {
        const acc = trigger.closest('.acc');
        this.toggleAcc(acc);
        if (acc.closest('[data-faq-list]') && acc.classList.contains('is-open')) this.setHash(`#faq/${acc.dataset.uid.replace('--', '/')}`);
        return;
      }
      const c = e.target.closest('[data-contact]');
      if (c) {
        e.preventDefault();
        this.openContact(c.dataset.contact, c.dataset.msg);
        return;
      }
      const f = e.target.closest('[data-faq]');
      if (f) {
        e.preventDefault();
        this.openFaq(f.dataset.faq);
        return;
      }
      // plain rich text links to #contact/... or #faq/...
      const a = e.target.closest('a[href*="#contact/"], a[href*="#faq/"]');
      if (a) {
        const act = this.parseAction(a);
        e.preventDefault();
        if (act.contact) this.openContact(act.contact, act.msg);
        else if (act.faq) this.openFaq(act.faq);
      }
    }

    route() {
      const [kind, ...rest] = location.hash.slice(1).split('/');
      const path = rest.map(decode).join('/');
      if (kind === 'faq' && path) this.openFaq(path);
      else if (kind === 'contact' && (!this.form || this.findTopic(path))) this.openContact(path);
    }

    // form switched off: the old Klantenservice section (template key "contact") takes its place
    contactSection() {
      const own = this.closest('.shopify-section');
      const keyed = document.querySelector('.shopify-section[id$="__contact"]');
      if (keyed) return keyed;
      let next = own && own.nextElementSibling;
      while (next && !next.offsetHeight) next = next.nextElementSibling;
      return next;
    }

    scrollToSection(el) {
      if (!el) return;
      el.style.scrollMarginTop = 'calc(var(--header-height, 0px) + 24px)';
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    /* ---------- contact form ---------- */
    // Shopify contact form (mails support@upfront.nl → Gorgias). We validate here, then
    // let the browser submit normally so Shopify's spam check can step in. The values are
    // kept in sessionStorage so the thank-you summary (or a retry after an error) can use them.
    findTopic(name) {
      const n = norm(name || '');
      return this.topics.find((t) => norm(t.name) === n);
    }

    store(data) {
      try {
        if (data) sessionStorage.setItem('support-hub-form', JSON.stringify(data));
        else return JSON.parse(sessionStorage.getItem('support-hub-form') || 'null');
      } catch (e) {
        return null;
      }
    }

    cleanUrl(hash = '#contact') {
      const url = new URL(location.href);
      url.searchParams.delete('contact_posted');
      url.searchParams.delete('form_type');
      url.hash = hash;
      return url.toString();
    }

    initForm() {
      this.form = this.$('form.sh-form');
      if (!this.form) return;
      this.form.noValidate = true;

      // after a successful post Shopify renders the thank-you state instead of the fields
      const success = this.$('[data-posted]', this.form);
      if (success) {
        this.fillSuccess(success, this.store());
        this.store({});
        this.$('[data-again]', success).addEventListener('click', () => (location.href = this.cleanUrl()));
        return;
      }

      this.topicBtn = this.$('[data-topic-btn]');
      this.topicList = this.$('[data-topic-list]');
      this.topicVal = this.$('[data-topic-val]');
      this.activeOpt = -1;
      const textarea = this.f('msg');

      this.topicBtn.addEventListener('click', () => this.openList(this.topicList.classList.contains('is-hidden')));
      this.topicBtn.addEventListener('keydown', (e) => {
        if (['ArrowDown', 'Enter', ' '].includes(e.key)) {
          e.preventDefault();
          this.openList(true);
        }
      });
      this.topicList.addEventListener('keydown', (e) => {
        const last = this.topics.length - 1;
        if (e.key === 'ArrowDown') this.markActive(Math.min(last, this.activeOpt + 1));
        else if (e.key === 'ArrowUp') this.markActive(Math.max(0, this.activeOpt - 1));
        else if (e.key === 'Enter' || e.key === ' ') {
          this.setTopic(this.topics[this.activeOpt]);
          this.openList(false);
          this.topicBtn.focus();
        } else if (e.key === 'Escape' || e.key === 'Tab') {
          this.openList(false);
          this.topicBtn.focus();
          return;
        } else return;
        e.preventDefault();
      });
      this.topicList.addEventListener('click', (e) => {
        const o = e.target.closest('.opt');
        if (!o) return;
        this.setTopic(this.topics[+o.dataset.i]);
        this.openList(false);
        this.topicBtn.focus();
      });

      textarea.addEventListener('input', () => (textarea.dataset.prefilled = '0'));
      this.form.addEventListener('input', (e) => e.target.closest('.wrap-f')?.classList.remove('is-error'));
      this.form.addEventListener('submit', (e) => this.submit(e));

      // Shopify sent us back with an error: put the answers back
      if (this.$('[data-send-error]', this.form)) {
        const saved = this.store();
        if (saved && saved.topic) {
          this.setTopic(this.findTopic(saved.topic));
          Object.entries(saved.values || {}).forEach(([k, val]) => {
            const el = this.f(k);
            if (el) el.value = val;
          });
        }
        this.$('#contact').scrollIntoView({ block: 'start' });
      }
    }

    f(name) {
      return this.$(`[data-f="${name}"]`, this.form);
    }

    openList(open) {
      if (!this.topicList) return;
      this.topicList.classList.toggle('is-hidden', !open);
      this.topicBtn.setAttribute('aria-expanded', open);
      if (open) {
        this.markActive(Math.max(0, this.topics.indexOf(this.topic)));
        this.topicList.focus();
      }
    }

    markActive(i) {
      this.activeOpt = i;
      this.$$('.opt', this.topicList).forEach((o, j) => o.classList.toggle('is-active', j === i));
      const opt = this.$$('.opt', this.topicList)[i];
      if (opt) this.topicList.setAttribute('aria-activedescendant', opt.id);
    }

    w(name) {
      return this.$(`[data-w="${name}"]`, this.form);
    }

    setTopic(cfg) {
      if (!cfg) return;
      this.topic = cfg;
      this.$('[data-topic-input]', this.form).value = cfg.name;
      this.topicVal.textContent = cfg.name;
      this.topicVal.classList.remove('c4');
      this.$$('.opt', this.topicList).forEach((o, i) => o.setAttribute('aria-selected', this.topics[i] === cfg));
      this.w('topic').classList.remove('is-error');
      this.$('[data-topic-fields]').classList.remove('is-hidden');

      const notice = this.$('[data-notice]');
      notice.classList.toggle('is-hidden', !cfg.notice);
      if (cfg.notice) {
        this.$('p', notice).textContent = cfg.notice.text;
        const a = this.$('a', notice);
        a.classList.toggle('is-hidden', !cfg.notice.href);
        if (cfg.notice.href) {
          const act = this.parseAction(Object.assign(document.createElement('a'), { href: cfg.notice.href }));
          a.href = cfg.notice.href;
          a.target = act.ext ? '_blank' : '';
          a.rel = act.ext ? 'noopener' : '';
          this.$('span', a).textContent = cfg.notice.link;
        }
      }
      this.$('[data-order-lbl]').textContent = cfg.order === 'req' ? 'Ordernummer *' : 'Ordernummer (optioneel)';
      ['batch', 'tht'].forEach((n) => {
        this.w(n).classList.toggle('is-hidden', !cfg.batch);
        // hidden fields are not sent, so they don't show up empty in the mail
        this.f(n).disabled = !cfg.batch;
      });
      this.$('[data-batch-hint]').classList.toggle('is-hidden', !cfg.batch);
      this.w('upload').classList.toggle('is-hidden', !cfg.upload);
      if (cfg.upload) {
        this.$('[data-up-title]').textContent = cfg.upload[0];
        this.$('[data-up-sub]').textContent = cfg.upload[1];
      }
      this.$('[data-msg-lbl]').textContent = cfg.msgLbl || 'Je bericht *';
      this.f('msg').placeholder = cfg.msgPh || 'Beschrijf kort wat er aan de hand is';
      this.$$('.wrap-f.is-error', this.form).forEach((w) => {
        if (w.closest('.is-hidden')) w.classList.remove('is-error');
      });
    }

    openContact(name, msg) {
      if (!this.form) {
        this.setHash('#contact');
        this.scrollToSection(this.contactSection());
        return;
      }
      const cfg = this.findTopic(name) || this.findTopic('Anders') || this.topics[0];
      // on the thank-you state there are no fields: reload a clean page on that topic
      if (!this.form || !this.topicBtn) {
        location.href = this.cleanUrl(`#contact/${encodeURIComponent(cfg.name)}`);
        return;
      }
      this.setTopic(cfg);
      const ta = this.f('msg');
      if (msg && (!ta.value.trim() || ta.dataset.prefilled === '1')) {
        ta.value = msg;
        ta.dataset.prefilled = '1';
      }
      this.setHash(`#contact/${encodeURIComponent(cfg.name)}`);
      this.$('#contact').scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => {
        const first = this.$$('input:not([type=hidden]), textarea', this.$('[data-topic-fields]')).find(
          (el) => !el.closest('.is-hidden') && !el.value.trim()
        );
        (first || ta).focus({ preventScroll: true });
      }, 500);
    }

    submit(e) {
      if (!this.topic) {
        e.preventDefault();
        this.w('topic').classList.add('is-error');
        this.topicBtn.focus();
        return;
      }
      const cfg = this.topic;
      const v = (n) => (this.f(n).value || '').trim();
      const checks = [
        ['naam', v('naam')],
        ['email', /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v('email'))],
        ['order', cfg.order !== 'req' || v('order')],
        ['batch', !cfg.batch || v('batch')],
        ['tht', !cfg.batch || v('tht')],
        ['msg', cfg.msgOpt || v('msg')],
      ];
      let firstBad = null;
      for (const [id, ok] of checks) {
        const w = this.w(id);
        w.classList.toggle('is-error', !ok);
        if (!ok && !firstBad) firstBad = w;
      }
      if (firstBad) {
        e.preventDefault();
        this.$('input, textarea', firstBad).focus();
        return;
      }

      // optional message left empty: still give the ticket a body
      if (!v('msg')) this.f('msg').value = cfg.name;
      const values = {};
      ['naam', 'email', 'order', 'batch', 'tht', 'msg'].forEach((n) => (values[n] = this.f(n).value));
      this.store({ topic: cfg.name, values });
      this.$('[data-submit]').disabled = true;
      // no preventDefault: the browser posts the form to Shopify
    }

    fillSuccess(box, saved) {
      if (!saved || !saved.values) return;
      const val = saved.values;
      const fill = (s) =>
        esc(s)
          .replace('[name]', esc((val.naam || '').trim().split(' ')[0]))
          .replace('[email]', esc((val.email || '').trim()));
      this.$('[data-success-title]', box).innerHTML = fill(this.config.successTitle);
      this.$('[data-success-text]', box).innerHTML = fill(this.config.successText);
      const rows = [['Onderwerp', saved.topic], ['Naam', val.naam], ['E-mail', val.email]];
      if (val.order) rows.push(['Ordernummer', val.order]);
      if (val.batch || val.tht) rows.push(['Batchcode / THT', `${val.batch} · ${val.tht}`]);
      const dl = this.$('[data-summary]', box);
      dl.innerHTML = rows.map(([k, x]) => `<dt class="c3">${esc(k)}</dt><dd>${esc(x || '')}</dd>`).join('');
      dl.classList.remove('is-hidden');
    }
  }


  customElements.define('support-hub', SupportHub);
}
